import { httpClient } from '@/utils/httpClient';
import { subscribeResourceMutated } from '@/utils/resourceEvents';
import type { NotifyEnvelope } from '@/types/api';
import type {
  AccountListItem,
  AccountProviderListItem,
  MetaTemplateVariablesResult,
  MetaWhatsappTemplate,
  NotificationChannel,
  NotificationTestPayload,
  NotificationTestResponse,
  TemplateOption,
  TemplateVariable,
  WhatsAppProviderConfigForAccount,
  WhatsAppProviderType,
} from '../types/notificationTest';
import { CHANNEL_ENDPOINT, PROVIDER_TYPE } from '../constants';

const accountIdHeader = (accountId: string): { 'account-id': string } => ({
  'account-id': accountId,
});

let inflightAllAccounts: Promise<AccountListItem[]> | null = null;

// accountId -> accountName, populated whenever the accounts list is fetched.
// Used to stamp every admin-ui test send with the sending account name.
const accountNameById = new Map<string, string>();

subscribeResourceMutated((resource) => {
  if (resource === 'apps/accounts') inflightAllAccounts = null;
});

export const getAllAccounts = (): Promise<AccountListItem[]> => {
  if (inflightAllAccounts) return inflightAllAccounts;
  inflightAllAccounts = httpClient
    .get<NotifyEnvelope<AccountListItem[]>>('/account')
    .then(({ data }) => {
      const list = data.Response ?? [];
      list.forEach((a) => accountNameById.set(a.id, a.accountName));
      return list;
    })
    .catch((err) => {
      inflightAllAccounts = null;
      throw err;
    });
  return inflightAllAccounts;
};

/**
 * Stamp a test send with its origin. Every send from the admin UI is an
 * "admin-test", and originAccount is the account it was sent on behalf of
 * (the sending account) — resolved from the accounts cache. Explicit values
 * on the body win.
 */
const withOrigin = <T extends object>(
  accountId: string,
  body: T
): T & { origin: string; originAccount?: string } => ({
  ...body,
  origin: (body as { origin?: string }).origin ?? 'admin-test',
  originAccount:
    (body as { originAccount?: string }).originAccount ??
    accountNameById.get(accountId),
});

export const getAccountsApplications = async (
  accountId: string
): Promise<string[]> => {
  const { data } = await httpClient.get<
    NotifyEnvelope<{ applications: string[] }>
  >('/v1/accountData/applications', {
    headers: accountIdHeader(accountId),
  });
  return data.Response?.applications ?? [];
};

export const getAccountsTemplates = async (params: {
  accountId: string;
  application: string;
  channel: string;
}): Promise<TemplateOption[]> => {
  const qs = new URLSearchParams({
    application: params.application,
    notificationChannel: params.channel,
  }).toString();
  const { data } = await httpClient.get<
    NotifyEnvelope<{ templates: TemplateOption[] }>
  >(`/v1/accountData/templates/list?${qs}`, {
    headers: accountIdHeader(params.accountId),
  });
  return data.Response?.templates ?? [];
};

export const getAccountTemplateVariables = async (params: {
  accountId: string;
  templateId: string;
}): Promise<TemplateVariable[]> => {
  const qs = new URLSearchParams({ templateId: params.templateId }).toString();
  const { data } = await httpClient.get<
    NotifyEnvelope<{ variables_in_template: TemplateVariable[] }>
  >(`/v1/accountData/template/variables?${qs}`, {
    headers: accountIdHeader(params.accountId),
  });
  return data.Response?.variables_in_template ?? [];
};

export const getAccountsGroups = async (params: {
  accountId: string;
  application: string;
}): Promise<string[]> => {
  const qs = new URLSearchParams({
    application: params.application,
  }).toString();
  const { data } = await httpClient.get<
    NotifyEnvelope<{ groups: string[] }>
  >(`/v1/accountData/groups?${qs}`, {
    headers: accountIdHeader(params.accountId),
  });
  return data.Response?.groups ?? [];
};

interface MetaTemplatesEnvelopeInner {
  success: boolean;
  data?: {
    success?: boolean;
    templates?: MetaWhatsappTemplate[];
    nextCursor?: string | null;
    prevCursor?: string | null;
  };
  errorCode?: string | null;
  message?: string | null;
}

export const getMetaWhatsappTemplates = async (params: {
  accountId: string;
  limit?: number;
  after?: string;
  before?: string;
}): Promise<MetaWhatsappTemplate[]> => {
  const pageLimit = params.limit ?? 100;

  const fetchPage = async (after?: string) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(pageLimit));
    if (after) qs.set('after', after);
    if (params.before) qs.set('before', params.before);
    const suffix = qs.toString();
    const { data } = await httpClient.get<
      NotifyEnvelope<MetaTemplatesEnvelopeInner>
    >(
      `/v1/notifications/whatsapp/templates${suffix ? `?${suffix}` : ''}`,
      { headers: accountIdHeader(params.accountId) }
    );
    return data.Response?.data;
  };

  const first = await fetchPage(params.after);
  const all: MetaWhatsappTemplate[] = [...(first?.templates ?? [])];

  // An explicit cursor (after/before) means the caller wants that single page.
  if (params.after || params.before) return all;

  // Meta paginates the message_templates endpoint (default 25/page). Follow the cursor so a
  // long list (e.g. 4+ templates × 7 languages, plus extras) isn't truncated to the first page —
  // that dropped qr_acceso's it/fr/es. Bounded loop as a runaway guard.
  let cursor = first?.nextCursor ?? undefined;
  for (let page = 1; page < 20 && cursor; page++) {
    const next = await fetchPage(cursor);
    const templates = next?.templates ?? [];
    if (templates.length === 0) break;
    all.push(...templates);
    cursor = next?.nextCursor ?? undefined;
  }
  return all;
};

interface MetaTemplateVariablesEnvelopeInner {
  success: boolean;
  data?: MetaTemplateVariablesResult;
  errorCode?: string | null;
  message?: string | null;
}

export const getMetaTemplateVariables = async (params: {
  accountId: string;
  name: string;
  language?: string;
}): Promise<MetaTemplateVariablesResult> => {
  const qs = new URLSearchParams({ name: params.name });
  if (params.language) qs.set('language', params.language);
  const { data } = await httpClient.get<
    NotifyEnvelope<MetaTemplateVariablesEnvelopeInner>
  >(`/v1/notifications/whatsapp/templates/variables?${qs.toString()}`, {
    headers: accountIdHeader(params.accountId),
  });
  return (
    data.Response?.data ?? {
      success: false,
      variables: [],
    }
  );
};

export const getProvidersByAccountId = async (
  accountId: string
): Promise<AccountProviderListItem[]> => {
  const { data } = await httpClient.get<
    NotifyEnvelope<AccountProviderListItem[]>
  >(`/provider/accountId/${accountId}`);
  return data.Response ?? [];
};

export const getWhatsAppProviderConfigForAccount = async (
  accountId: string
): Promise<WhatsAppProviderConfigForAccount> => {
  const providers = await getProvidersByAccountId(accountId);
  const available: WhatsAppProviderType[] = [];
  if (providers.some((p) => p.providerType === PROVIDER_TYPE.TwilioWhatsapp)) {
    available.push('twilio');
  }
  if (providers.some((p) => p.providerType === PROVIDER_TYPE.MetaWhatsapp)) {
    available.push('meta');
  }
  return { available };
};

export const sendTestNotification = async (
  channel: NotificationChannel,
  body: NotificationTestPayload
): Promise<NotificationTestResponse> => {
  const endpoint = `/v1/notifications/${CHANNEL_ENDPOINT[channel]}`;
  const { data } = await httpClient.post<
    NotifyEnvelope<NotificationTestResponse>
  >(endpoint, withOrigin(body.accountId, body), {
    headers: accountIdHeader(body.accountId),
  });
  return data.Response ?? { success: false };
};

export const sendPushTestNotification = async (
  slug: 'webNotification' | 'appNotification',
  body: { accountId: string } & Record<string, unknown>
): Promise<NotificationTestResponse> => {
  const { data } = await httpClient.post<
    NotifyEnvelope<NotificationTestResponse>
  >(`/v1/push/${slug}`, withOrigin(body.accountId, body), {
    headers: accountIdHeader(body.accountId),
  });
  return data.Response ?? { success: false };
};

export interface TeamChatTestBody {
  accountId: string;
  appId: string;
  target: string;
  webhookUrls: string[];
  title?: string;
  text: string;
  severity?: string;
  messageType: string;
  webhookUrl?: string;
  origin?: string;
}

export const sendTeamChatTestNotification = async (
  body: TeamChatTestBody
): Promise<NotificationTestResponse> => {
  const { accountId, ...rest } = body;
  const { data } = await httpClient.post<
    NotifyEnvelope<NotificationTestResponse>
  >('/v1/notifications/team-chat', withOrigin(accountId, rest), {
    headers: accountIdHeader(accountId),
  });
  return data.Response ?? { success: false };
};

export interface BotChatTestBody {
  accountId: string;
  appId: string;
  recipients: { type: 'INDIVIDUAL'; userIds: string[] };
  message: { type: 'CUSTOM'; content: string };
  messageType: string;
  webhookUrl?: string;
  origin?: string;
}

export const sendBotChatTestNotification = async (
  channel: 'viber' | 'line',
  body: BotChatTestBody
): Promise<NotificationTestResponse> => {
  const { accountId, ...rest } = body;
  const { data } = await httpClient.post<
    NotifyEnvelope<NotificationTestResponse>
  >(`/v1/notifications/${channel}`, withOrigin(accountId, rest), {
    headers: accountIdHeader(accountId),
  });
  return data.Response ?? { success: false };
};

export interface InboxTestBody {
  accountId: string;
  appId: string;
  recipients: { type: 'INDIVIDUAL'; userIds: string[] };
  message: { title?: string; content: string; link?: string };
  origin?: string;
}

export interface InboxListResult {
  userId: string;
  unreadCount: number;
  recordsTotal: number;
  data: Array<{
    id: string;
    title: string | null;
    body: string;
    link: string | null;
    origin: string | null;
    isRead: boolean;
    readAt: string | null;
    createdAt: string;
  }>;
}

export const sendInboxTestNotification = async (
  body: InboxTestBody
): Promise<NotificationTestResponse & { stored?: number }> => {
  const { accountId, ...rest } = body;
  const { data } = await httpClient.post<
    NotifyEnvelope<NotificationTestResponse & { stored?: number }>
  >('/v1/notifications/inbox', withOrigin(accountId, rest), {
    headers: accountIdHeader(accountId),
  });
  return data.Response ?? { success: false };
};

export const listInbox = async (
  accountId: string,
  userId: string
): Promise<InboxListResult> => {
  const { data } = await httpClient.get<NotifyEnvelope<InboxListResult>>(
    `/v1/inbox/${encodeURIComponent(userId)}?start=0&length=25`,
    { headers: accountIdHeader(accountId) }
  );
  return data.Response ?? { userId, unreadCount: 0, recordsTotal: 0, data: [] };
};

export const markInboxAllRead = async (
  accountId: string,
  userId: string
): Promise<void> => {
  await httpClient.patch(
    `/v1/inbox/${encodeURIComponent(userId)}/read`,
    {},
    { headers: accountIdHeader(accountId) }
  );
};

export interface WebhookTestBody {
  accountId: string;
  appId: string;
  webhookUrls: string[];
  payload: unknown;
  secret?: string;
  event?: string;
  origin?: string;
}

export const sendWebhookTestNotification = async (
  body: WebhookTestBody
): Promise<NotificationTestResponse> => {
  const { accountId, ...rest } = body;
  const { data } = await httpClient.post<
    NotifyEnvelope<NotificationTestResponse>
  >('/v1/notifications/webhook', withOrigin(accountId, rest), {
    headers: accountIdHeader(accountId),
  });
  return data.Response ?? { success: false };
};
